import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  FormErrorMessage,
  Stack,
  useToast,
} from "@chakra-ui/react";

const DEFAULT_API_BASE = "https://api.globalwineinstitute.org";
const DEFAULT_API_KEY = "f3bb4afa55070cc052d1b888d4f7465a";

const CATEGORY_OPTIONS = [
  { value: "data", label: "Data / datasets" },
  { value: "collaboration", label: "Collaboration / co-authorship" },
  { value: "student_project", label: "Student project / thesis" },
  { value: "participation", label: "Call for Papers" },
  { value: "jobs", label: "Jobs" },
  { value: "opportunity", label: "Academic opportunity" },
  { value: "other", label: "Other" },
];

const NOTICE_TYPE_OPTIONS = [
  { value: "general", label: "General notice" },
  { value: "co_author_request", label: "Co-author request" },
];

function NoticeForm({
  isOpen,
  onClose,
  initialNotice = null,
  onSaved,
  apiBaseUrl = DEFAULT_API_BASE,
  apiKey = DEFAULT_API_KEY,
  currentUserId = null,
}) {
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("collaboration");
  const [websiteLink, setWebsiteLink] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [institution, setInstitution] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [noticeType, setNoticeType] = useState("general");
  const [researchTheme, setResearchTheme] = useState("");
  const [expertiseTags, setExpertiseTags] = useState("");
  const [roleNeeded, setRoleNeeded] = useState("");
  const [methods, setMethods] = useState("");
  const [region, setRegion] = useState("");
  const [availability, setAvailability] = useState("");
  const [timeline, setTimeline] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditMode = Boolean(initialNotice && initialNotice.notice_id);

  useEffect(() => {
    if (initialNotice && isOpen) {
      setTitle(initialNotice.title || "");
      setDescription(initialNotice.description || "");
      setCategory(initialNotice.category || "collaboration");
      setWebsiteLink(initialNotice.website_link || "");
      setContactEmail(initialNotice.contact_email || "");
      setInstitution(initialNotice.institution || "");
      const rawNoticeType =
        initialNotice.notice_type || initialNotice.noticeType || "";
      const hasCoAuthorDetails = Boolean(
        initialNotice.role_needed ||
          initialNotice.roleNeeded ||
          initialNotice.expertise_tags ||
          initialNotice.expertiseTags ||
          initialNotice.availability ||
          initialNotice.timeline
      );
      setNoticeType(
        rawNoticeType
          ? rawNoticeType
          : hasCoAuthorDetails
          ? "co_author_request"
          : "general"
      );
      setResearchTheme(initialNotice.research_theme || initialNotice.theme || "");
      const rawTags =
        initialNotice.expertise_tags || initialNotice.expertiseTags || "";
      const tagsValue = Array.isArray(rawTags)
        ? rawTags.join(", ")
        : String(rawTags || "");
      setExpertiseTags(tagsValue);
      setRoleNeeded(initialNotice.role_needed || initialNotice.roleNeeded || "");
      setMethods(initialNotice.methods || initialNotice.methodology || "");
      setRegion(initialNotice.region || "");
      setAvailability(initialNotice.availability || "");
      setTimeline(initialNotice.timeline || "");
      setExpiresOn(
        initialNotice.expires_on
          ? String(initialNotice.expires_on).slice(0, 10)
          : ""
      );
    } else if (isOpen && !initialNotice) {
      setTitle("");
      setDescription("");
      setCategory("collaboration");
      setWebsiteLink("");
      setContactEmail("");
      setInstitution("");
      setExpiresOn("");
      setNoticeType("general");
      setResearchTheme("");
      setExpertiseTags("");
      setRoleNeeded("");
      setMethods("");
      setRegion("");
      setAvailability("");
      setTimeline("");
      setErrors({});
    }
  }, [initialNotice, isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!description.trim()) newErrors.description = "Description is required";
    if (!category.trim()) newErrors.category = "Category is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    const isCoAuthorRequest = noticeType === "co_author_request";
    const noticeTypeValue = noticeType === "general" ? null : noticeType;
    const expertiseTagsValue = expertiseTags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)
      .join(", ");

    const payload = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      website_link: websiteLink.trim() || null,
      contact_email: contactEmail.trim() || null,
      institution: institution.trim() || null,
      expires_on: expiresOn || null,
      notice_type: noticeTypeValue,
      research_theme: isCoAuthorRequest ? researchTheme.trim() || null : null,
      expertise_tags: isCoAuthorRequest ? expertiseTagsValue || null : null,
      role_needed: isCoAuthorRequest ? roleNeeded.trim() || null : null,
      methods: isCoAuthorRequest ? methods.trim() || null : null,
      region: isCoAuthorRequest ? region.trim() || null : null,
      availability: isCoAuthorRequest ? availability.trim() || null : null,
      timeline: isCoAuthorRequest ? timeline.trim() || null : null,
    };

    if (!isEditMode && currentUserId) {
      payload.user_id = currentUserId;
    }

    const url = isEditMode
      ? `${apiBaseUrl}/notices/${initialNotice.notice_id}`
      : `${apiBaseUrl}/notices`;

    const method = isEditMode ? "PATCH" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": apiKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const message =
          data && data.error
            ? data.error
            : "An error occurred while saving the notice";
        throw new Error(message);
      }

      toast({
        title: isEditMode ? "Notice updated" : "Notice created",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (typeof onSaved === "function") {
        onSaved(data);
      }

      onClose();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save notice",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditMode ? "Edit notice" : "Create a new notice"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired isInvalid={Boolean(errors.title)}>
              <FormLabel>Title</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short, clear summary"
              />
              {errors.title && (
                <FormErrorMessage>{errors.title}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={Boolean(errors.description)}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What kind of data, collaboration, or help are you looking for?"
                rows={5}
              />
              {errors.description && (
                <FormErrorMessage>{errors.description}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={Boolean(errors.category)}>
              <FormLabel>Category</FormLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {errors.category && (
                <FormErrorMessage>{errors.category}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Notice type</FormLabel>
              <Select
                value={noticeType}
                onChange={(e) => setNoticeType(e.target.value)}
              >
                {NOTICE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {noticeType === "co_author_request" ? (
              <Stack spacing={4} borderWidth="1px" borderRadius="md" p={4}>
                <FormControl>
                  <FormLabel>Research theme</FormLabel>
                  <Input
                    value={researchTheme}
                    onChange={(e) => setResearchTheme(e.target.value)}
                    placeholder="e.g., Market competitiveness, branding"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Region</FormLabel>
                  <Input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="Global, Europe, Latin America, etc."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Role needed</FormLabel>
                  <Input
                    value={roleNeeded}
                    onChange={(e) => setRoleNeeded(e.target.value)}
                    placeholder="e.g., Co-author, data analyst"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Methods / data focus</FormLabel>
                  <Textarea
                    value={methods}
                    onChange={(e) => setMethods(e.target.value)}
                    placeholder="Key methods, datasets, or tools"
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Expertise tags</FormLabel>
                  <Input
                    value={expertiseTags}
                    onChange={(e) => setExpertiseTags(e.target.value)}
                    placeholder="Comma-separated tags"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Availability</FormLabel>
                  <Input
                    value={availability}
                    onChange={(e) => setAvailability(e.target.value)}
                    placeholder="e.g., 5 hours/week, Q3-Q4"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Timeline</FormLabel>
                  <Input
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    placeholder="e.g., Start in May, 6-month project"
                  />
                </FormControl>
              </Stack>
            ) : null}

            <FormControl>
              <FormLabel>Website link (optional)</FormLabel>
              <Input
                value={websiteLink}
                onChange={(e) => setWebsiteLink(e.target.value)}
                placeholder="URL with more information"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Contact email (optional)</FormLabel>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Email for replies"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Institution (optional)</FormLabel>
              <Input
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="Your university or organisation"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Expires on (optional)</FormLabel>
              <Input
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="gwbi"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            {isEditMode ? "Save changes" : "Create notice"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default NoticeForm;
